import { Data, Match } from 'effect';
import type { ProxyTypeEnum } from 'venn-api';
import type { SelectedProxy } from './utils';
import type { BulkPickerDetailedProxyMetadata } from '../../../hooks/useProxyDataQuery';
import {
  useHasFF,
  type SecondLayerProxyTypeValue,
  type SecondLayerProxyType,
  type ProxyTypeValue,
  createSecondLayerProxyTypeValue,
} from 'venn-utils';
import { DisabledProxyReason } from '../types';

type BaseProxyInfo = {
  selectedProxy: SelectedProxy | null;
  numLags: number;
};

export type LayerableProxyInfo = BaseProxyInfo & { proxyType: 'BACKFILL'; extrapolate: false };

export type NonLayerableProxyInfo = BaseProxyInfo &
  (Exclude<ProxyTypeValue, { proxyType: 'BACKFILL' }> | { proxyType: 'BACKFILL'; extrapolate: true });

type SecondLayerProxyInfo = BaseProxyInfo & SecondLayerProxyTypeValue;

export type LayeredProxyConfiguration = Data.TaggedEnum<{
  Empty: {};
  SingleLayerable: { proxy: LayerableProxyInfo };
  SingleNonLayerable: { proxy: NonLayerableProxyInfo };
  Multi: {
    firstLayer: LayerableProxyInfo;
    secondLayer: SecondLayerProxyInfo;
  };
}>;
export const LayeredProxyConfiguration = Data.taggedEnum<LayeredProxyConfiguration>();

export const createLayerableProxyInfo = (overrides: Partial<BaseProxyInfo> = {}): LayerableProxyInfo => ({
  proxyType: 'BACKFILL',
  extrapolate: false,
  selectedProxy: null,
  numLags: 0,
  ...overrides,
});

export const createNonLayerableProxyInfo = (
  proxyType: NonLayerableProxyInfo['proxyType'],
  extrapolate: boolean,
  overrides: Partial<BaseProxyInfo> = {},
): NonLayerableProxyInfo => {
  const baseInfo = { selectedProxy: null, numLags: 0, ...overrides };

  if (proxyType === 'SUBSTITUTE' || proxyType === 'EXTRAPOLATE') {
    return { proxyType, ...baseInfo };
  }

  if (proxyType === 'BACKFILL') {
    return { proxyType, extrapolate: true as const, ...baseInfo };
  }

  return { proxyType, extrapolate, ...baseInfo };
};

export const createSecondLayerProxyInfo = (
  proxyType: SecondLayerProxyType,
  extrapolate: boolean,
  overrides: Partial<BaseProxyInfo> = {},
): SecondLayerProxyInfo => ({
  ...createSecondLayerProxyTypeValue(proxyType, extrapolate),
  selectedProxy: null,
  numLags: 0,
  ...overrides,
});

export type TabState = Data.TaggedEnum<{
  Empty: { selectedProxy?: SelectedProxy | null };
  WithProxy: { proxyTypeValue: ProxyTypeValue; selectedProxy?: SelectedProxy | null };
}>;
export const TabState = Data.taggedEnum<TabState>();

export function deriveLayeredProxyConfig(
  proxyDataArray:
    | readonly []
    | readonly [BulkPickerDetailedProxyMetadata]
    | readonly [BulkPickerDetailedProxyMetadata, BulkPickerDetailedProxyMetadata],
): LayeredProxyConfiguration {
  if (proxyDataArray.some((proxy) => !proxy.proxyType || !proxy.proxyName)) {
    return LayeredProxyConfiguration.Empty();
  }

  return Match.value(proxyDataArray).pipe(
    Match.when(
      (arr: typeof proxyDataArray): arr is readonly [] => arr.length === 0,
      () => LayeredProxyConfiguration.Empty(),
    ),
    Match.when(
      (arr: typeof proxyDataArray): arr is readonly [BulkPickerDetailedProxyMetadata] => arr.length === 1,
      ([proxy]) => {
        const overrides: Partial<BaseProxyInfo> = {
          selectedProxy: proxy.proxyId ? { id: proxy.proxyId, name: proxy.proxyName } : null,
          numLags: proxy.numLags ?? 0,
        };

        if (proxy.proxyType === 'BACKFILL' && !proxy.extrapolate) {
          return LayeredProxyConfiguration.SingleLayerable({
            proxy: createLayerableProxyInfo(overrides),
          });
        }

        return LayeredProxyConfiguration.SingleNonLayerable({
          proxy: createNonLayerableProxyInfo(proxy.proxyType, proxy.extrapolate ?? false, overrides),
        });
      },
    ),
    Match.when(
      (arr): arr is readonly [BulkPickerDetailedProxyMetadata, BulkPickerDetailedProxyMetadata] => arr.length === 2,
      ([first, second]) => {
        if (first.proxyType !== 'BACKFILL' || first.extrapolate) {
          return LayeredProxyConfiguration.Empty();
        }

        return LayeredProxyConfiguration.Multi({
          firstLayer: createLayerableProxyInfo({
            selectedProxy: first.proxyId ? { id: first.proxyId, name: first.proxyName } : null,
            numLags: first.numLags ?? 0,
          }),
          secondLayer: createSecondLayerProxyInfo(
            second.proxyType as SecondLayerProxyType,
            second.extrapolate ?? false,
            {
              selectedProxy: second.proxyId ? { id: second.proxyId, name: second.proxyName } : null,
              numLags: second.numLags ?? 0,
            },
          ),
        });
      },
    ),
    Match.orElse(() => LayeredProxyConfiguration.Empty()),
  );
}

// ===== Legacy exports for backward compatibility =====

export function useProxyTypes() {
  const hasExtrapolationFf = useHasFF('extrapolation_ff');
  // Predefined arrays ensure reference stability
  return hasExtrapolationFf ? PROXY_TYPES_WITH_EXTRAPOLATION : STANDARD_PROXY_TYPES;
}

const proxyTypeReasons = [
  DisabledProxyReason.EXTRAPOLATE_QUARTERLY,
  DisabledProxyReason.BACKFILL_QUARTERLY,
  DisabledProxyReason.INTERPOLATE_NONQUARTERLY,
  DisabledProxyReason.UNAVAILABLE_DAILY,
  DisabledProxyReason.SUBSTITUTE_SYSTEM_FUND,
  DisabledProxyReason.EXTRAPOLATE_NOT_ENOUGH_DATA,
  DisabledProxyReason.DESMOOTH_NOT_ENOUGH_DATA,
  DisabledProxyReason.INTERPOLATE_NOT_ENOUGH_DATA,
];
type ProxyTypeReason = (typeof proxyTypeReasons)[number];

/**
 * Returns true if the provided reason is defined and is specific to the proxy type
 *
 * Other reasons may be due to other factors, such as the selected investment, and can be resolved by the user without changing the proxy type.
 */
export const isProxyTypeError = (reason?: DisabledProxyReason): reason is ProxyTypeReason => {
  return !!reason && proxyTypeReasons.includes(reason);
};

const STANDARD_PROXY_TYPES = [
  'BACKFILL',
  'SUBSTITUTE',
  'DESMOOTH_INTERPOLATE',
  'INTERPOLATE',
  'DESMOOTH',
] as const satisfies ProxyTypeEnum[];

export const PROXY_TYPES_WITH_EXTRAPOLATION = [
  ...STANDARD_PROXY_TYPES,
  'EXTRAPOLATE',
] as const satisfies ProxyTypeEnum[];
